import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  BrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import LoginPOS from "./LoginPOS";
import CompanySales from "./CompanySales";
import UltimosMovimientosPOS from "./UltimosMovimientosPOS";
import CompanyCashRegister from "./CompanyCashRegister";
import CompanyCashClosings from "./CompanyCashClosings";
import CompanyProducts from "./CompanyProducts";
import CompanyInventory from "./CompanyInventory";
import POSElectronicInvoices from "./POSElectronicInvoices";
import POSProfileModal from "./POSProfileModal";
import POSSessionMonitor from "./POSSessionMonitor";
import POSClients from "./POSClients";
import CompanyCategories from "./CompanyCategories";

import {
  CompanyBrandingProvider,
} from "./CompanyBrandingContext";

import {
  PermissionProvider,
  usePermissions,
} from "./PermissionContext";

import {
  api,
  logoutPosSession,
} from "./api";

import POSSessionGuard from "./POSSessionGuard";

import "./POSLayout.css";


function validColor(value, fallback) {
  if (
    typeof value === "string" &&
    value.trim() &&
    !value.includes("var(") &&
    typeof CSS !== "undefined" &&
    CSS.supports("color", value.trim())
  ) {
    return value.trim();
  }

  return fallback;
}


function getErrorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    const message = detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");

    if (message) return message;
  }

  return (
    "No fue posible cerrar la sesión en el servidor. " +
    "Revisa la conexión e intenta nuevamente."
  );
}


function ModuleIcon({ type }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      width="28"
      height="28"
    >
      {type === "sales" && (
        <>
          <path d="M3 3h2l2.4 12h11.2l2-8H6" />
          <path d="M8 15l-1 3h12" />
          <circle cx="9" cy="21" r="1" />
          <circle cx="18" cy="21" r="1" />
        </>
      )}

      {type === "cash" && (
        <>
          <rect
            x="5"
            y="3"
            width="14"
            height="7"
            rx="2"
          />

          <path d="M8 6.5h5M7 10l-3 5v6h16v-6l-3-5" />
          <path d="M4 16h16M9 19h6M9 13h.01M12 13h.01M15 13h.01" />
        </>
      )}

      {type === "closings" && (
        <>
          <rect
            x="4"
            y="5"
            width="16"
            height="16"
            rx="2"
          />

          <path d="M8 3v4M16 3v4M4 10h16M8 15l3 3 5-5" />
        </>
      )}

      {type === "movements" && (
        <>
          <path d="M3 11a9 9 0 1 1 2.6 7.4" />
          <path d="M3 5v6h6M12 7v5l3 2" />
        </>
      )}

      {type === "inventory" && (
        <>
          <path d="M12 2 3 7l9 5 9-5-9-5Z" />
          <path d="M3 7v10l9 5 9-5V7M12 12v10" />
          <path d="m7.5 4.5 9 5" />
        </>
      )}

      {type === "products" && (
        <>
          <path d="M3 3h9l9 9-9 9-9-9V3Z" />
          <circle cx="7.5" cy="7.5" r="1" />
        </>
      )}

      {type === "warehouse" && (
        <>
          <path d="M3 21V8l9-5 9 5v13H3Z" />
          <path d="M7 21V11h10v10M7 15h10M7 18h10" />
        </>
      )}

      {type === "electronic-invoices" && (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
          <path d="M14 2v6h6M8 12h8M8 16h5" />
        </>
      )}

      {type === "customers" && (
        <>
          <circle cx="9" cy="8" r="3" />
          <path d="M3 21v-2a6 6 0 0 1 12 0v2" />
          <path d="M16 5a3 3 0 0 1 0 6" />
          <path d="M21 21v-2a6 6 0 0 0-4-5.65" />
        </>
      )}
      {type === "categories" && (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </>
      )}
    </svg>
  );
}


function POSModuleLink({
  to,
  label,
  icon,
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `pos-module-link${isActive ? " pos-module-active" : ""
        }`
      }
      aria-label={label}
    >
      <ModuleIcon type={icon} />

      <span
        className="pos-module-tooltip"
        aria-hidden="true"
      >
        {label}
      </span>
    </NavLink>
  );
}


function POSHeader({
  company,
  user,
  logout,
  loggingOut,
  refreshPermissions,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { can } = usePermissions();

  const [branding, setBranding] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);

  const userMenuRef = useRef(null);
  const userTriggerRef = useRef(null);

  const inventoryMenuRef = useRef(null);
  const inventoryTriggerRef = useRef(null);

  const basePath = `/${encodeURIComponent(company)}`;

  const canViewProducts = can("products.view");
  const canViewInventory = can("inventory.view");
  const canViewElectronicInvoices = can("billing.view");

  const showInventoryMenu =
    canViewProducts || canViewInventory;

  const inventoryRouteActive =
    location.pathname === `${basePath}/productos` ||
    location.pathname.startsWith(`${basePath}/productos/`) ||
    location.pathname === `${basePath}/inventario` ||
    location.pathname.startsWith(`${basePath}/inventario/`);
  location.pathname === `${basePath}/categorias` ||
    location.pathname.startsWith(`${basePath}/categorias/`);

  const [showProfileModal, setShowProfileModal] = useState(false);

  const [updatedProfile, setUpdatedProfile] = useState(null);

  const displayedUser =
    updatedProfile?.id === user.id
      ? { ...user, ...updatedProfile }
      : user;

  const closeProfileModal = useCallback(() => {
    setShowProfileModal(false);
  }, []);

  function openProfileModal() {
    if (loggingOut) return;

    setOpenMenu(null);
    setShowProfileModal(true);
  }

  function handleProfileSaved(data) {
    setUpdatedProfile({
      id: user.id,
      name: data.name,
      email: data.email,
    });

    setShowProfileModal(false);

    toast.success(
      data.message || "Tus datos se actualizaron correctamente."
    );
  }

  useEffect(() => {
    const controller = new AbortController();

    setBranding(null);
    setOpenMenu(null);

    api
      .get(
        `/companies/by-subdomain/${encodeURIComponent(
          company
        )}/branding`,
        {
          signal: controller.signal,
        }
      )
      .then(({ data }) => {
        if (!controller.signal.aborted) {
          setBranding({
            ...data,
            subdomain: company,
          });
        }
      })
      .catch(() => {
        // Se conservan los colores predeterminados.
      });

    return () => controller.abort();
  }, [company]);

  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openMenu) return undefined;

    const containerRef =
      openMenu === "user"
        ? userMenuRef
        : inventoryMenuRef;

    const triggerRef =
      openMenu === "user"
        ? userTriggerRef
        : inventoryTriggerRef;

    function handleOutside(event) {
      if (!containerRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setOpenMenu(null);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener("pointerdown", handleOutside);
    document.addEventListener("focusin", handleOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handleOutside
      );

      document.removeEventListener(
        "focusin",
        handleOutside
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [openMenu]);

  const currentBranding =
    branding?.subdomain === company
      ? branding
      : null;

  const companyName =
    currentBranding?.commercial_name ||
    currentBranding?.company_name ||
    user.company_name ||
    company;

  const primary = validColor(
    currentBranding?.primary_color,
    "#1f5d7a"
  );

  const secondary = validColor(
    currentBranding?.secondary_color,
    primary
  );

  const userName = displayedUser.name || "Usuario";

  const initials = userName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

  function toggleMenu(menu) {
    if (loggingOut) return;

    setOpenMenu((current) =>
      current === menu ? null : menu
    );
  }

  function goToSales() {
    setOpenMenu(null);
    navigate(`${basePath}/ventas`);
  }

  function updatePermissions() {
    if (loggingOut) return;

    setOpenMenu(null);
    userTriggerRef.current?.focus();
    refreshPermissions();
  }

  function handleLogout() {
    if (loggingOut) return;

    setOpenMenu(null);
    logout();
  }

  return (
    <header
      className="pos-topbar"
      style={{
        "--pos-nav-primary": primary,
        "--pos-nav-secondary": secondary,
      }}
    >
      <div className="pos-topbar-inner">
        <div className="pos-topbar-brand">
          <strong>NetLedger POS</strong>

          <span
            className="pos-topbar-divider"
            aria-hidden="true"
          >
            /
          </span>

          <span
            className="pos-topbar-company"
            title={companyName}
          >
            {companyName}
          </span>
        </div>

        <nav
          className="pos-topbar-modules"
          aria-label="Módulos del POS"
        >
          <POSModuleLink
            to={`${basePath}/ventas`}
            label="Ventas"
            icon="sales"
          />

          <POSModuleLink
            to={`${basePath}/caja`}
            label="Caja"
            icon="cash"
          />

          <POSModuleLink
            to={`${basePath}/cierres-caja`}
            label="Cierres de caja"
            icon="closings"
          />

          <POSModuleLink
            to={`${basePath}/clientes`}
            label="Clientes"
            icon="customers"
          />

          <POSModuleLink
            to={`${basePath}/ultimos-movimientos`}
            label="Últimos movimientos"
            icon="movements"
          />

          {showInventoryMenu && (
            <div
              className="pos-inventory-menu"
              ref={inventoryMenuRef}
            >
              <button
                ref={inventoryTriggerRef}
                type="button"
                className={
                  `pos-module-link pos-inventory-trigger${inventoryRouteActive ||
                    openMenu === "inventory"
                    ? " pos-module-active"
                    : ""
                  }`
                }
                aria-label="Inventario"
                aria-expanded={openMenu === "inventory"}
                aria-controls="pos-inventory-dropdown"
                onClick={() => toggleMenu("inventory")}
                disabled={loggingOut}
              >
                <ModuleIcon type="inventory" />

                <span
                  className="pos-inventory-chevron"
                  aria-hidden="true"
                >
                  ▾
                </span>

                {openMenu !== "inventory" && (
                  <span
                    className="pos-module-tooltip"
                    aria-hidden="true"
                  >
                    Inventario
                  </span>
                )}
              </button>

              {openMenu === "inventory" && (
                <div
                  id="pos-inventory-dropdown"
                  className="pos-inventory-dropdown"
                >
                  <div className="pos-inventory-heading">
                    Inventario
                  </div>

                  {canViewProducts && (
                    <NavLink
                      to={`${basePath}/productos`}
                      className={({ isActive }) =>
                        `pos-inventory-option${isActive
                          ? " pos-inventory-option-active"
                          : ""
                        }`
                      }
                      onClick={() => setOpenMenu(null)}
                    >
                      <ModuleIcon type="products" />

                      <span>
                        <strong>Productos</strong>
                        <small>Catálogo de productos</small>
                      </span>
                    </NavLink>
                  )}
                  {canViewProducts && (
                    <NavLink
                      to={`${basePath}/categorias`}
                      className={({ isActive }) =>
                        `pos-inventory-option${isActive
                          ? " pos-inventory-option-active"
                          : ""
                        }`
                      }
                      onClick={() => setOpenMenu(null)}
                    >
                      <ModuleIcon type="categories" />

                      <span>
                        <strong>Categorías</strong>
                        <small>
                          Clasificación de productos
                        </small>
                      </span>
                    </NavLink>
                  )}

                  {canViewInventory && (
                    <NavLink
                      to={`${basePath}/inventario`}
                      className={({ isActive }) =>
                        `pos-inventory-option${isActive
                          ? " pos-inventory-option-active"
                          : ""
                        }`
                      }
                      onClick={() => setOpenMenu(null)}
                    >
                      <ModuleIcon type="warehouse" />

                      <span>
                        <strong>Inventario</strong>
                        <small>Existencias y movimientos</small>
                      </span>
                    </NavLink>
                  )}
                </div>
              )}
            </div>
          )}

          {canViewElectronicInvoices && (
            <POSModuleLink
              to={`${basePath}/facturacion-electronica`}
              label="Facturación electrónica"
              icon="electronic-invoices"
            />
          )}
        </nav>

        <div
          className="pos-topbar-user"
          ref={userMenuRef}
        >
          <button
            ref={userTriggerRef}
            type="button"
            className="pos-user-trigger"
            aria-expanded={openMenu === "user"}
            aria-controls="pos-user-dropdown"
            onClick={() => toggleMenu("user")}
            disabled={loggingOut}
          >
            <span
              className="pos-user-avatar"
              aria-hidden="true"
            >
              {initials}
            </span>

            <span className="pos-user-text">
              <strong>{userName}</strong>

              <small>
                {loggingOut
                  ? "Cerrando sesión…"
                  : "Sesión POS"}
              </small>
            </span>

            <span
              className={
                `pos-user-chevron ${openMenu === "user"
                  ? "pos-user-chevron-open"
                  : ""
                }`
              }
              aria-hidden="true"
            >
              ▾
            </span>
          </button>

          {openMenu === "user" && (
            <div
              id="pos-user-dropdown"
              className="pos-user-dropdown"
            >
              <div className="pos-user-dropdown-heading">
                <strong>{userName}</strong>

                {displayedUser.email && (
                  <span>{displayedUser.email}</span>
                )}

                <small>{companyName}</small>
              </div>

              <button
                type="button"
                className="pos-user-option"
                onClick={goToSales}
                disabled={loggingOut}
              >
                <span aria-hidden="true">▦</span>
                Ventas
              </button>

              <button
                type="button"
                className="pos-user-option"
                onClick={openProfileModal}
                disabled={loggingOut}
              >
                <span aria-hidden="true">✎</span>
                Actualizar mis datos
              </button>

              <button
                type="button"
                className="pos-user-option"
                onClick={updatePermissions}
                disabled={loggingOut}
              >
                <span aria-hidden="true">↻</span>
                Actualizar permisos
              </button>

              <div className="pos-user-menu-divider" />

              <button
                type="button"
                className="pos-user-option pos-user-logout"
                onClick={handleLogout}
                disabled={loggingOut}
              >
                <span aria-hidden="true">↪</span>

                {loggingOut
                  ? "Cerrando sesión…"
                  : "Cerrar sesión"}
              </button>
            </div>
          )}
        </div>
      </div>
      {showProfileModal && (
        <POSProfileModal
          company={company}
          user={displayedUser}
          primary={primary}
          onClose={closeProfileModal}
          onSaved={handleProfileSaved}
        />
      )}
    </header>
  );
}


function ProtectedPOS() {
  const { company } = useParams();
  const navigate = useNavigate();

  const logoutInProgress = useRef(false);

  const [loggingOut, setLoggingOut] = useState(false);

  const {
    user,
    error,
    loadingPermissions,
    refreshPermissions,
  } = usePermissions();

  async function logout() {
    if (logoutInProgress.current) return;

    logoutInProgress.current = true;
    setLoggingOut(true);

    try {
      await logoutPosSession(company);

      navigate(
        `/${encodeURIComponent(company)}`,
        {
          replace: true,
        }
      );
    } catch (requestError) {
      toast.error(getErrorMessage(requestError));
    } finally {
      logoutInProgress.current = false;
      setLoggingOut(false);
    }
  }

  if (loadingPermissions) {
    return (
      <div
        className="container py-5"
        role="status"
      >
        Validando sesión…
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div
          className="alert alert-danger"
          role="alert"
        >
          {error}
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => refreshPermissions()}
            disabled={loggingOut}
          >
            Reintentar
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={logout}
            disabled={loggingOut}
          >
            {loggingOut
              ? "Cerrando sesión…"
              : "Cerrar sesión"}
          </button>
        </div>
      </div>
    );
  }

  if (!user || user.subdomain !== company) {
    return (
      <Navigate
        to={`/${encodeURIComponent(company)}`}
        replace
      />
    );
  }

  return (
    <>
      <POSSessionMonitor />

      <POSHeader
        company={company}
        user={user}
        logout={logout}
        loggingOut={loggingOut}
        refreshPermissions={refreshPermissions}
      />

      {loggingOut && (
        <div
          className="container py-2"
          role="status"
          aria-live="polite"
        >
          Cerrando la sesión POS…
        </div>
      )}

      <main className="p-3 p-lg-4">
        <Outlet />
      </main>
    </>
  );
}


function POSProtectedLayout() {
  const { company } = useParams();

  return (
    <PermissionProvider key={company}>
      <CompanyBrandingProvider>
        <ProtectedPOS />
      </CompanyBrandingProvider>
    </PermissionProvider>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <POSSessionGuard />

      <Routes>
        <Route
          path="/"
          element={
            <div className="container py-5">
              <h1 className="h3">
                NetLedger POS
              </h1>

              <p>
                Abre la dirección del POS de tu empresa.
                Ejemplo: /netsuperhelados
              </p>
            </div>
          }
        />

        <Route
          path="/:company"
          element={<LoginPOS />}
        />

        <Route
          path="/:company"
          element={<POSProtectedLayout />}
        >
          <Route
            path="ventas"
            element={<CompanySales />}
          />

          <Route
            path="caja"
            element={<CompanyCashRegister />}
          />

          <Route
            path="cierres-caja"
            element={<CompanyCashClosings />}
          />

          <Route
            path="clientes"
            element={<POSClients />}
          />

          <Route
            path="ultimos-movimientos"
            element={<UltimosMovimientosPOS />}
          />

          <Route
            path="productos"
            element={<CompanyProducts />}
          />

          <Route
            path="categorias"
            element={<CompanyCategories />}
          />

          <Route
            path="inventario"
            element={<CompanyInventory />}
          />

          <Route
            path="facturacion-electronica"
            element={<POSElectronicInvoices />}
          />
        </Route>

        <Route
          path="*"
          element={
            <div className="container py-5">
              Página no encontrada.
            </div>
          }
        />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3500}
      />
    </BrowserRouter>
  );
}